import type {
  ChatMessage,
  ChatStatus,
  DataViews,
  SQLAgentSource,
  SelectedSources,
} from "./chat";
import { filterDisplayableAgentSources } from "@/utils/chatSourceFilters";

export const DATA_SOURCES = ["vivo_data_views", "protocol", "plot"] as const;

export const CHAT_STATUS = {
  PENDING: "pending",
  COMPLETE: "complete",
  ERROR: "error",
} as const;

type LegacySelectedSources = SelectedSources & {
  trial_pdf?: Record<string, unknown>;
  pdf?: Record<string, unknown>;
  SQLAgentSchemas?: SQLAgentSource[];
  vivo_data_views?: DataViews[] & {
    requested_clarification?: boolean;
  };
};

export function normalizeChatStatus(chat: ChatMessage = {}): ChatStatus {
  if (chat.status) {
    return chat.status;
  }
  if (chat.isLoading) {
    return CHAT_STATUS.PENDING;
  }
  if (chat.hasError) {
    return CHAT_STATUS.ERROR;
  }
  if (chat.output) {
    return CHAT_STATUS.COMPLETE;
  }
  return CHAT_STATUS.ERROR;
}

export function normalizeChatMessage(chat: ChatMessage = {}): ChatMessage {
  const status = normalizeChatStatus(chat);

  return {
    ...chat,
    output: chat.output || chat.message || "",
    selected_sources: chat.selected_sources || {},
    status,
    isLoading: chat.isLoading ?? false,
    hasError: chat.hasError ?? status === CHAT_STATUS.ERROR,
  };
}

export function normalizeChatHistory(history: ChatMessage[] = []) {
  return history.map((chat) => normalizeChatMessage(chat));
}

export function applyErrorResponseToChat(chat: ChatMessage, error: unknown) {
  const responseData =
    error &&
    typeof error === "object" &&
    "responseData" in error &&
    error.responseData &&
    typeof error.responseData === "object"
      ? error.responseData
      : {};

  const typedResponseData = responseData as Partial<ChatMessage>;

  chat.output =
    typedResponseData.message ||
    "There was an error fetching an answer to your question. Please try again.";
  chat.selected_sources = typedResponseData.selected_sources || {};
  chat.timestamp = typedResponseData.timestamp || chat.timestamp;
  chat.requested_clarification =
    typedResponseData.requested_clarification ?? false;
  chat.status = typedResponseData.status || CHAT_STATUS.ERROR;
  chat.isLoading = false;
  chat.hasError = true;
}

export function hasNoUsableSources(chat?: ChatMessage) {
  const sources = chat?.selected_sources as LegacySelectedSources | undefined;
  if (!sources) return true;

  const trialPdf = sources.trial_pdf || sources.pdf || {};
  const sql = filterDisplayableAgentSources(
    sources.SQLAgentSchema || sources.SQLAgentSchemas,
  );
  const protocol = filterDisplayableAgentSources(sources.ProtocolAgentSchema);
  const plot = filterDisplayableAgentSources(sources.PlotAgentSchema);
  const dataViews = sources.vivo_data_views || {};

  const dataViewsEmpty = Object.keys(dataViews).length === 0;
  const trialPdfEmpty = Object.keys(trialPdf).length === 0;
  const sqlEmpty = sql.length === 0;
  const protocolEmpty = protocol.length === 0;
  const plotEmpty = plot.length === 0;

  return (
    trialPdfEmpty && sqlEmpty && protocolEmpty && plotEmpty && dataViewsEmpty
  );
}

export function isAwaitingClarification(chat?: ChatMessage) {
  const sources = chat?.selected_sources as LegacySelectedSources | undefined;

  if (sources?.vivo_data_views?.requested_clarification !== undefined) {
    return sources.vivo_data_views.requested_clarification === true;
  }

  if (chat?.requested_clarification !== undefined) {
    return chat.requested_clarification === true;
  }

  return false;
}
